import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { crmDeepLink } from '@/lib/links.js';
import { fmtCents, fmtPct, todayEasternYmd } from '@/components/money/format.js';

// THE A/P DOOR (TRACON-MONEY-SPEC §7.4): pick the payee, tick the transactions
// this payment covers, type the amount you actually sent. Allocation amounts
// PREFILL from what is owed (a DERIVED value: premium + tax − commission, so
// prefill is allowed); the TYPED fact is the total, and it must match to the
// cent or the server refuses with both figures. Before confirm the screen says
// which ticked rows were paid into a different account than the one you are
// paying from (R11) and which will be recorded as fronted (R16: the client has
// not paid). Every record is followed by a read back.
//
// REFUND MODE (?refund=<id>, from /money/refunds): the payee is the CLIENT
// (payee_kind 'client'), the amount is typed and must equal the refund to the
// cent, and the record goes through the refund door's settle-by-payment,
// which calls the same A/P writer. No allocations: a refund is one row.

const selectCls = 'mt-1 h-11 w-full rounded-card border border-border bg-bg px-2 text-sm text-text';

function RefundPay({ refundId, accounts, onDone }) {
  const [refund, setRefund] = useState(null);
  const [error, setError] = useState('');
  const [account, setAccount] = useState('');
  const [paidOn, setPaidOn] = useState(todayEasternYmd());
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [mismatch, setMismatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const accountName = (id) => accounts.find((x) => x.id === id)?.name || 'an unknown account';

  useEffect(() => {
    let alive = true;
    api.get('/api/money/refunds?all=1').then((r) => {
      if (!alive) return;
      const row = (r.refunds || []).find((x) => x.id === refundId);
      if (!row) setError('That refund is not on file.');
      else setRefund(row);
    }).catch((e) => alive && setError(e.message || 'Could not load the refund'));
    return () => { alive = false; };
  }, [refundId]);

  const submit = async () => {
    setBusy(true);
    setMismatch(null);
    try {
      const r = await api.post(`/api/money/refunds/${refundId}/settle-payment`, { paid_from_account_id: account, paid_on: paidOn, reference: reference || null, amount: Number(amount) });
      toast.success(`Refund of ${fmtCents(r.refund?.amount ?? refund.amount)} to ${refund.client || 'the client'} recorded from ${accountName(account)} on ${paidOn}.`);
      onDone();
    } catch (e) {
      if (e.status === 409 && e.body?.code === 'REFUND_AMOUNT_MISMATCH') { setMismatch(e.body); return; }
      toast.error(e.body?.code || e.message || 'Could not record the refund');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="mt-4 text-sm text-danger">{error} <Link to="/money/refunds" className="underline">Back to refunds</Link></p>;
  if (!refund) return <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p>;
  if (refund.settled_at) return <p className="mt-4 text-sm text-text-muted">This refund was already settled ({refund.settled_by}). <Link to="/money/refunds" className="underline">Back to refunds</Link></p>;
  const t = refund.transaction;
  return (
    <div className="mt-4 rounded-card border border-border bg-surface p-4">
      <div className="text-sm">
        <span className="font-medium text-text">Payee: {refund.client || 'client not on file'}</span>
        {t ? <span className="ml-2 text-xs text-text-dim">{t.type} {t.effective_date}</span> : null}
        {t ? <a href={crmDeepLink('Deals', t.crm_deal_id)} target="_blank" rel="noreferrer" className="ml-2 text-xs text-text-muted hover:underline">deal</a> : null}
      </div>
      <div className="mt-1 font-mono text-[11.5px] text-text-muted">
        refund due {fmtCents(refund.amount)} · commission clawback {fmtCents(refund.commission_clawback)}{t ? <> · source <span className="font-mono">{String(t.id).slice(0, 8)}</span></> : null}
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-4">
        <label className="text-xs text-text-muted">
          Paid from
          <select value={account} onChange={(e) => setAccount(e.target.value)} className={selectCls}>
            <option value="">choose account</option>
            {accounts.filter((a) => a.active).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-text-muted">
          Day it left
          <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="mt-1" />
        </label>
        <label className="text-xs text-text-muted">
          Bank memo / reference
          <Input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1" />
        </label>
        <label className="text-xs text-text-muted">
          Amount you sent
          <Input type="number" step="0.01" inputMode="decimal" data-money-input="refund_amount_paid" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button onClick={submit} disabled={busy || !account || !paidOn || amount === ''}>{busy ? 'Recording...' : 'Record client refund'}</Button>
        <Link to="/money/refunds" className="text-sm text-text-muted hover:underline">Back to refunds</Link>
      </div>
      {mismatch ? (
        <div className="mt-3 rounded-card border border-danger/40 bg-danger/5 p-3 text-sm">
          <div className="font-medium text-danger">Not recorded: the amount you typed is not the refund.</div>
          <div className="mt-1 font-mono text-xs text-text">Refund {fmtCents(mismatch.refund)} · Sent {fmtCents(mismatch.typed)} · Difference {fmtCents(mismatch.difference)}</div>
        </div>
      ) : null}
    </div>
  );
}

export default function MoneyPay() {
  const [params, setParams] = useSearchParams();
  const refundId = params.get('refund') || '';
  const [payees, setPayees] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [unpaid, setUnpaid] = useState(null);
  const [history, setHistory] = useState([]);
  const [statements, setStatements] = useState([]);
  const [statementId, setStatementId] = useState('');
  const [shortPay, setShortPay] = useState(null);
  const [shortPayReason, setShortPayReason] = useState('');
  const [error, setError] = useState('');

  const [payee, setPayee] = useState('');
  const [account, setAccount] = useState('');
  const [paidOn, setPaidOn] = useState(todayEasternYmd());
  const [reference, setReference] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [allocations, setAllocations] = useState({}); // id -> amount string
  const [mismatch, setMismatch] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [p, a, u, h, s] = await Promise.all([
        api.get('/api/money/pay/payees'),
        api.get('/api/money/accounts'),
        api.get('/api/money/pay/unpaid'),
        api.get('/api/money/carrier-payments'),
        api.get('/api/money/statements'),
      ]);
      setPayees(p.payees || []);
      setAccounts(a.accounts || []);
      setUnpaid(u);
      setHistory(h.payments || []);
      setStatements((s.statements || []).filter((x) => Number(x.open_balance) !== 0));
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the pay screen');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const rows = unpaid?.rows || [];
  const accountName = (id) => accounts.find((x) => x.id === id)?.name || 'an unknown account';
  const selectedIds = Object.keys(allocations);
  const allocatedTotal = useMemo(
    () => Math.round(selectedIds.reduce((acc, id) => acc + (Number(allocations[id]) || 0), 0) * 100) / 100,
    [allocations, selectedIds],
  );
  const selectedRows = rows.filter((r) => allocations[r.id] !== undefined);
  const mismatches = selectedRows.filter((r) => r.ar_account_id && account && r.ar_account_id !== account);
  const fronts = selectedRows.filter((r) => r.ar_payment_status !== 'succeeded');

  // §8.3: choosing a statement prefills the payee and the allocations from its
  // CONFIRMED lines (a human matched each one). The typed total still has to
  // equal the open balance, or carry a reason.
  const chooseStatement = async (id) => {
    setStatementId(id);
    setShortPay(null);
    setShortPayReason('');
    if (!id) return;
    const s = statements.find((x) => x.statement_id === id);
    if (s) setPayee(String(s.carrier_id));
    try {
      const r = await api.get(`/api/money/statements/${id}/confirmed-lines`);
      const next = {};
      for (const l of r.lines || []) if (l.policy_transaction_id) next[l.policy_transaction_id] = String(l.net_due);
      setAllocations(next);
      if (!(r.lines || []).length) toast.info('This statement has no confirmed lines yet. Confirm lines on the Statements screen, or tick rows by hand.');
    } catch (e) {
      toast.error(e.message || 'Could not load the statement lines');
    }
  };

  const toggle = (row) => setAllocations((a) => {
    const n = { ...a };
    if (n[row.id] !== undefined) delete n[row.id];
    else n[row.id] = row.remaining != null ? String(row.remaining) : '';
    return n;
  });

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = r.carrier_name || 'carrier not on the snapshot';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return [...m.entries()].sort((x, y) => x[0].localeCompare(y[0]));
  }, [rows]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/money/carrier-payments', {
        payee_carrier_id: Number(payee),
        amount: Number(amountPaid),
        paid_from_account_id: account,
        paid_on: paidOn,
        reference: reference || null,
        statement_id: statementId || null,
        short_pay_reason: shortPay ? shortPayReason : undefined,
        allocations: selectedIds.map((id) => ({ policy_transaction_id: id, amount: Number(allocations[id]) })),
      });
      toast.success(`Recorded ${fmtCents(r.payment.amount)} to the payee on ${paidOn}: ${r.allocations.length} allocation(s)${r.fronts.length ? `, ${r.fronts.length} fronted` : ''}.`);
      for (const f of r.fronts || []) toast.info(`Fronted ${fmtCents(f.amount)} on deal ${f.crm_deal_id}: the client's payment is ${f.ar_payment_status}.`);
      for (const m of r.account_mismatches || []) toast.info(`Deal ${m.crm_deal_id} was paid into ${accountName(m.ar_account_id)}; this payment left ${accountName(account)}.`);
      setAllocations({});
      setAmountPaid('');
      setReference('');
      setMismatch(null);
      setStatementId('');
      setShortPay(null);
      setShortPayReason('');
      await refresh(); // a confirmation is a read back, not an echo
    } catch (e) {
      if (e.status === 400 && e.body?.code === 'ALLOCATION_SUM_MISMATCH') { setMismatch(e.body); return; }
      if (e.status === 400 && e.body?.code === 'STATEMENT_AMOUNT_MISMATCH') { setShortPay(e.body); return; }
      toast.error(e.body?.code || e.message || 'Could not record the payment');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = payee && account && paidOn && amountPaid !== '' && selectedIds.length > 0 && !busy && (!shortPay || shortPayReason.trim().length > 0);

  if (refundId) {
    return (
      <div className="p-4">
        <div>
          <h2 className="text-xl font-semibold text-text">Pay a client refund</h2>
          <p className="mt-1 text-sm text-text-muted">One transfer to the client for one return. Type what you sent; it must be the refund to the cent.</p>
        </div>
        {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
        <RefundPay refundId={refundId} accounts={accounts} onDone={async () => { setParams({}); await refresh(); }} />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Pay a carrier</h2>
        <p className="mt-1 text-sm text-text-muted">
          One transfer, one payee, the transactions it covers. Type what you sent; the allocations must add up to it.
        </p>
      </div>

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {unpaid === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}

      {unpaid ? (
        <>
          <div className="mt-4 grid gap-3 rounded-card border border-border bg-surface p-4 md:grid-cols-4">
            <label className="text-xs text-text-muted md:col-span-4">
              Paying a statement? (prefills the payee and the confirmed lines)
              <select value={statementId} onChange={(e) => chooseStatement(e.target.value)} className={selectCls}>
                <option value="">no statement, pay to bind</option>
                {statements.map((s) => <option key={s.statement_id} value={s.statement_id}>{s.carrier_name || s.carrier_id}{s.system_label ? ` ${s.system_label}` : ''} · period end {s.period_end} · open {fmtCents(s.open_balance)}</option>)}
              </select>
            </label>
            <label className="text-xs text-text-muted">
              Payee
              <select value={payee} onChange={(e) => setPayee(e.target.value)} className={selectCls}>
                <option value="">choose payee</option>
                {payees.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_cobroker ? ' (wholesaler)' : ''}</option>)}
              </select>
            </label>
            <label className="text-xs text-text-muted">
              Paid from
              <select value={account} onChange={(e) => setAccount(e.target.value)} className={selectCls}>
                <option value="">choose account</option>
                {accounts.filter((a) => a.active).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-text-muted">
              Day it left
              <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="mt-1" />
            </label>
            <label className="text-xs text-text-muted">
              Bank memo / reference
              <Input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1" />
            </label>
          </div>

          <section className="mt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">
              Not yet remitted: {unpaid.count} transaction{unpaid.count === 1 ? '' : 's'}
              {unpaid.unpriced_count ? <span className="ml-2 text-danger">({unpaid.unpriced_count} with no rate stated: owed unknown, nothing prefilled)</span> : null}
            </h3>
            {grouped.map(([carrier, list]) => (
              <div key={carrier} className="mt-3">
                <div className="text-sm font-medium text-text">{carrier}</div>
                <ul className="mt-1 divide-y divide-border rounded-card border border-border bg-surface px-3">
                  {list.map((row) => (
                    <li key={row.id} className="flex items-start gap-3 py-3">
                      <input type="checkbox" checked={allocations[row.id] !== undefined} onChange={() => toggle(row)} className="mt-1 h-4 w-4" aria-label={`cover ${row.counterparty || row.crm_deal_id}`} />
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-medium text-text">{row.counterparty || 'counterparty not on file'}</span>
                          <span className="text-xs text-text-dim">{row.type} {row.effective_date}</span>
                          <a href={crmDeepLink('Deals', row.crm_deal_id)} target="_blank" rel="noreferrer" className="text-xs text-text-muted hover:underline">deal</a>
                        </div>
                        <div className="mt-0.5 font-mono text-[11.5px] text-text-muted">
                          premium {fmtCents(row.premium)} · tax {fmtCents(row.tax)} · rate {fmtPct(row.commission_rate)} · commission {fmtCents(row.commission)} · owed {fmtCents(row.owed)} · sent so far {fmtCents(row.allocated)}
                          {row.ar_payment_status !== 'succeeded' ? <span className="text-danger"> · client payment {row.ar_payment_status}: paying now is a front</span> : null}
                          {row.ar_account_id ? <span> · client paid into {accountName(row.ar_account_id)}</span> : null}
                        </div>
                      </div>
                      {allocations[row.id] !== undefined ? (
                        <Input
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          value={allocations[row.id]}
                          onChange={(e) => setAllocations((a) => ({ ...a, [row.id]: e.target.value }))}
                          className="w-32"
                          aria-label="allocation amount"
                        />
                      ) : (
                        <div className="shrink-0 font-mono text-sm text-text">{fmtCents(row.remaining)}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>

          <div className="mt-4 rounded-card border border-border bg-surface p-4">
            <div className="text-sm text-text-muted">
              {selectedIds.length} allocation{selectedIds.length === 1 ? '' : 's'}, adding to <span className="font-mono text-text">{fmtCents(allocatedTotal)}</span>
            </div>
            {mismatches.length ? (
              <div className="mt-2 text-sm text-danger">
                {mismatches.length} of {selectedIds.length} were paid into {accountName(mismatches[0].ar_account_id)}. Pay from {accountName(mismatches[0].ar_account_id)}, or record the receivable knowingly.
              </div>
            ) : null}
            {fronts.length ? (
              <div className="mt-2 text-sm text-danger">
                {fronts.length} of {selectedIds.length} will be recorded as fronted: the client has not paid.
              </div>
            ) : null}
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              <label className="text-xs text-text-muted">
                Amount you sent
                <Input type="number" step="0.01" inputMode="decimal" data-money-input="amount_paid" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} className="mt-1" />
              </label>
              <div className="flex items-end md:col-span-2">
                <Button onClick={submit} disabled={!canSubmit} className="w-full">{busy ? 'Recording...' : 'Record carrier payment'}</Button>
              </div>
            </div>
            {shortPay ? (
              <div className="mt-3 rounded-card border border-danger/40 bg-danger/5 p-3 text-sm">
                <div className="font-medium text-danger">Not recorded: the amount differs from the statement's open balance.</div>
                <div className="mt-1 font-mono text-xs text-text">
                  Open {fmtCents(shortPay.open_balance)} · Sent {fmtCents(shortPay.amount)} · Difference {fmtCents(shortPay.difference)}
                </div>
                <p className="mt-2 text-xs text-text-muted">A short-pay or a credit taken is fine when it is said. Type why; the reason is kept beside both figures.</p>
                <Input data-money-input="short_pay_reason" value={shortPayReason} onChange={(e) => setShortPayReason(e.target.value)} className="mt-2" />
              </div>
            ) : null}
            {mismatch ? (
              <div className="mt-3 rounded-card border border-danger/40 bg-danger/5 p-3 text-sm">
                <div className="font-medium text-danger">Not recorded: the allocations do not add up to the amount you sent.</div>
                <div className="mt-1 font-mono text-xs text-text">
                  Sent {fmtCents(mismatch.amount)} · Allocated {fmtCents(mismatch.allocated)} · Difference {fmtCents(mismatch.difference)}
                </div>
              </div>
            ) : null}
          </div>

          <section className="mt-8 border-t border-border pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">Recorded payments</h3>
            {history.length === 0 ? <p className="mt-2 text-sm text-text-muted">None yet.</p> : (
              <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {history.map((p) => (
                  <li key={p.id} className="py-2 text-sm">
                    <span className="font-mono text-text">{fmtCents(p.amount)}</span>
                    <span className="ml-2 text-text-muted">{p.paid_on} · payee {payees.find((x) => String(x.id) === String(p.payee_carrier_id))?.name || p.payee_carrier_id} · from {accountName(p.paid_from_account_id)} · {p.allocations.length} allocation(s){p.reference ? ` · ${p.reference}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
